import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { i18n } from '../i18n';
import {
  LANGUAGE_STORAGE_KEY,
  normalizeAppLanguage,
  type AppLanguage,
} from '../i18n/constants';

export type { AppLanguage };

type LanguageContextValue = {
  language: AppLanguage;
  setLanguage: (l: AppLanguage) => void;
};

const Ctx = createContext<LanguageContextValue | undefined>(undefined);

function readLanguage(): AppLanguage {
  return normalizeAppLanguage(i18n.language || localStorage.getItem(LANGUAGE_STORAGE_KEY));
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<AppLanguage>(() => readLanguage());

  useEffect(() => {
    const onLanguageChanged = (lng: string) => {
      const next = normalizeAppLanguage(lng);
      setLanguageState(next);
      document.documentElement.setAttribute('data-lang', next);
      try {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, next);
      } catch {
        // ignore
      }
    };

    onLanguageChanged(i18n.language);
    i18n.on('languageChanged', onLanguageChanged);
    return () => {
      i18n.off('languageChanged', onLanguageChanged);
    };
  }, []);

  const setLanguage = useCallback((l: AppLanguage) => {
    void i18n.changeLanguage(l);
  }, []);

  const value = useMemo<LanguageContextValue>(() => ({ language, setLanguage }), [language, setLanguage]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useLanguage() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useLanguage must be used within LanguageProvider');
  return v;
}
